import { computed, watch } from "vue";
import { useStore } from "vuex";

import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";

import type { IntegrationFunder } from "@/models/clientCredentials";
import type { ClientCredentialsRules, IClient } from "@/models/clients";
import type { ApiServiceRequestData } from "@/models/integrations";
import type { IUser } from "@/models/users";

export const useIntegrations = () => {
  const { getters, dispatch } = useStore();
  const { isClientAdmin } = useAuth();

  const user = computed<IUser | null>(() => getters["auth/user"]);
  const client = computed<IClient>(
    () =>
      (isClientAdmin && getters["auth/authClientSettings"]) ||
      getters["clients/active"]
  );
  const byocRules = computed<ClientCredentialsRules>(
    () => getters["clients/activeClientCredentialsRules"]
  );
  const clientFunderIntegrations = computed<IntegrationFunder[]>(() => {
    const topFunders = ["bhg", "ondeck", "revenued"];
    const integrations: IntegrationFunder[] =
      getters["clients/activeClientFunderIntegrations"];

    const configuredIntegrations: IntegrationFunder[] = [];
    const topFunderIntegrations: IntegrationFunder[] = [];
    const availableIntegrations: IntegrationFunder[] = [];
    const rest: IntegrationFunder[] = [];

    for (const integration of integrations) {
      if (integration.state === "configured") {
        configuredIntegrations.push(integration);
        continue;
      }

      if (
        topFunders.some((topFunder) =>
          integration.funder_name.toLowerCase().includes(topFunder)
        )
      ) {
        topFunderIntegrations.push(integration);
        continue;
      }

      if (integration.state === "available") {
        availableIntegrations.push(integration);
        continue;
      }

      rest.push(integration);
    }

    return [
      ...configuredIntegrations,
      ...topFunderIntegrations,
      ...availableIntegrations,
      ...rest,
    ];
  });

  const update = async (params: ApiServiceRequestData) => {
    if (!client.value) {
      return;
    }
    const actionName = isClientAdmin
      ? "auth/updateAuthClientSettings"
      : "clients/updateClient";

    const data = {
      id: client.value.id,
      ...params,
    };

    await dispatch(actionName, isClientAdmin ? data : { data });
  };

  const getClientsCredentialsValues = async () => {
    if (!client.value) {
      return;
    }
    await dispatch("clients/getClientCredentialsValues", client.value.id);
  };

  const { fetchWrapper: getUpdatedClientInfo, loading: isLoading } =
    usePromiseWrapper(async (updatedUser) => {
      if (!updatedUser?.client_id) {
        return;
      }
      await dispatch("clients/find", updatedUser.client_id);
      if (!client.value) {
        return;
      }
      await Promise.all([
        dispatch("clients/getClientCredentialsRules", client.value.id),
        getClientsCredentialsValues(),
      ]);
    });

  const getClientFunderIntegrations = async () => {
    if (!client.value) {
      return;
    }
    await dispatch("clients/getClientFunderIntegrations", client.value.id);
  };

  const {
    fetchWrapper: getUpdatedClientFunderIntegrations,
    loading: isFunderIntegrationsLoading,
  } = usePromiseWrapper(async () => {
    await getClientFunderIntegrations();
  });

  watch(user, getUpdatedClientInfo, { immediate: true });
  watch(user, getUpdatedClientFunderIntegrations, { immediate: true });

  return {
    isLoading,
    isFunderIntegrationsLoading,
    user,
    client,
    byocRules,
    clientFunderIntegrations,
    update,
  };
};
